import type { PointerEvent, ReactNode } from "react";

export type Tone = "cyan" | "amber" | "red" | "green" | "violet" | "neutral";

const toneClass = (tone: Tone, prefix: string): string =>
  tone === "neutral" ? "" : ` ${prefix}--${tone}`;

export interface StatProps {
  label: string;
  value: ReactNode;
  big?: boolean;
  tone?: Tone;
  title?: string;
}

export type StatValueSize = "regular" | "compact" | "tight" | "micro";

/**
 * Ledger columns do not grow with money. Step plain numeric text down before a
 * new comma group can hide its final digits; rich React values manage their own
 * wrapping instead.
 */
export function statValueSize(value: ReactNode): StatValueSize {
  if (typeof value !== "string" && typeof value !== "number" && typeof value !== "bigint") {
    return "regular";
  }
  const length = String(value).length;
  if (length >= 15) return "micro";
  if (length >= 12) return "tight";
  if (length >= 9) return "compact";
  return "regular";
}

const STAT_VALUE_FONT_SIZE: Record<StatValueSize, string | undefined> = {
  regular: undefined,
  compact: "var(--fs-base)",
  tight: "var(--fs-sm)",
  micro: "var(--fs-micro)",
};

const STAT_COLOR: Record<Tone, string | undefined> = {
  cyan: "var(--cyan)",
  amber: "var(--amber)",
  red: "var(--red)",
  green: "var(--green)",
  violet: "var(--violet)",
  neutral: undefined,
};

export function Stat({ label, value, big, tone = "neutral", title }: StatProps) {
  const size = statValueSize(value);
  return (
    <div className={big ? "stat stat--big" : "stat"} title={title}>
      <span className="label">{label}</span>
      <span
        className={`stat__value stat__value--${size}`}
        style={{ color: STAT_COLOR[tone], fontSize: STAT_VALUE_FONT_SIZE[size] }}
      >
        {value}
      </span>
    </div>
  );
}

export interface BarProps {
  /** 0..1 after division by max. */
  value: number;
  max?: number;
  tone?: Tone;
  title?: string;
}

export function Bar({ value, max = 1, tone = "cyan", title }: BarProps) {
  const ratio = max <= 0 ? 0 : Math.max(0, Math.min(1, value / max));
  return (
    <div className={`bar${toneClass(tone, "bar")}`} title={title}>
      <div className="bar__fill" style={{ width: `${ratio * 100}%` }} />
    </div>
  );
}

export interface PillProps {
  tone?: Tone;
  title?: string;
  children: ReactNode;
}

export function Pill({ tone = "neutral", title, children }: PillProps) {
  return (
    <span className={`pill${toneClass(tone, "pill")}`} title={title}>
      {children}
    </span>
  );
}

export type ButtonTone = "default" | "primary" | "buy" | "sell" | "danger" | "gift";

export interface ButtonProps {
  /** Optional: a press-and-hold control drives itself from the pointer instead. */
  onClick?: () => void;
  /**
   * Pointer handlers, for controls that repeat while held. Spread straight onto
   * the element — a hold control must see the press and the release, and a
   * click handler fires too late to start repeating and too early to stop.
   */
  onPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerUp?: () => void;
  onPointerLeave?: () => void;
  onPointerCancel?: () => void;
  children: ReactNode;
  tone?: ButtonTone;
  small?: boolean;
  disabled?: boolean;
  /** Shown on hover — used to explain why a disabled action is unavailable. */
  title?: string;
}

export function Button({
  onClick,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  children,
  tone = "default",
  small,
  disabled,
  title,
}: ButtonProps) {
  const cls = ["btn", tone === "default" ? "" : `btn--${tone}`, small ? "btn--sm" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
      onPointerCancel={onPointerCancel}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// formatting
// ---------------------------------------------------------------------------

const GROUPED = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

export function formatMoney(value: number): string {
  const sign = value < 0 ? "-" : "";
  return `${sign}$${GROUPED.format(Math.abs(Math.round(value)))}`;
}

/** Compact form used in dense rows: 24K, 1.2M. */
export function formatMoneyShort(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
  return `${sign}$${Math.round(abs)}`;
}

export function formatClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/*
 * `formatEndReason` used to live here. It now lives in `i18n/serverText.ts`
 * with the rest of the Korean, because three places print the end reason — the
 * result card, the session-ended banner and the world log — and the log was
 * printing the raw English while the other two printed the Korean.
 */
